import React, { useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../libs/axios';
import Breadcrumb from '../../utils/breadcrumbs';

const User360: React.FC = () => {
  const [userId, setUserId] = useState('');
  const [nameQuery, setNameQuery] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [data, setData] = useState<any>(null);
  const [footprint, setFootprint] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [hiding, setHiding] = useState(false);

  const searchByName = async () => {
    const q = nameQuery.trim();
    if (!q) { toast.error('Type a name to search'); return; }
    setSearching(true);
    try {
      const res: any = await api.get(`/users?name=${encodeURIComponent(q)}&limit=15`);
      setResults(res.data?.data || []);
      if ((res.data?.data || []).length === 0) toast('No users match that name');
    } catch (e) { toast.error('Search failed'); }
    finally { setSearching(false); }
  };

  const load = async (id?: string) => {
    const uid = (id ?? userId).toString().trim();
    if (!uid) { toast.error('Enter a user ID'); return; }
    setUserId(uid);
    setResults([]);
    setLoading(true);
    setData(null);
    setFootprint(null);
    try {
      const res: any = await api.get(`/users/${uid}/overview`);
      setData(res.data);
      const fp: any = await api.get(`/moderation/users/${uid}/footprint`);
      setFootprint(fp.data);
    } catch (e) { toast.error('User not found'); }
    finally { setLoading(false); }
  };

  const ban = async (suspend: boolean) => {
    try {
      await api.post(`/moderation/${suspend ? 'ban' : 'unban'}-user`, { user_id: Number(userId) });
      toast.success(suspend ? 'Suspended' : 'Reinstated');
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const hideFootprint = async (hidden: boolean) => {
    if (!footprint) return;
    const verb = hidden ? 'HIDE' : 'RESTORE';
    const msg = hidden
      ? `Hide ALL of this user's footprint?\n\n• ${footprint.matches_total} matches\n• ${footprint.teams} teams\n• ${footprint.players} players\n\nThey disappear from stats, leaderboards and listings. This is reversible.`
      : `Restore this user's footprint (${footprint.matches_total} matches, ${footprint.teams} teams, ${footprint.players} players)?`;
    if (!window.confirm(msg)) return;
    setHiding(true);
    try {
      const res: any = await api.post('/moderation/hide-user-footprint', { user_id: Number(userId), hidden });
      const r = res.data;
      toast.success(`${verb}: ${r.matches_updated} matches, ${r.teams_updated} teams, ${r.players_updated} players`);
      load();
    } catch (e) { toast.error(`Failed to ${verb.toLowerCase()} footprint`); }
    finally { setHiding(false); }
  };

  const d = data;
  const name = d?.details ? [d.details.first_name, d.details.last_name].filter(Boolean).join(' ') : '';
  const fpHidden = footprint?.is_hidden;

  return (
    <div className="p-4 sm:p-6">
      <Breadcrumb />
      <div className="mt-4 mb-6">
        <h1 className="text-xl font-semibold text-gray-900">User 360</h1>
        <p className="text-sm text-gray-500">Support view: profile, name history, teams, tournaments, status, and footprint moderation.</p>
      </div>

      {/* Search: by name or by id */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="flex gap-2 flex-1">
          <input
            value={nameQuery}
            onChange={(e) => setNameQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && searchByName()}
            placeholder="Search by player name"
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm flex-1"
          />
          <button onClick={searchByName} className="bg-gray-800 hover:bg-gray-900 text-white text-sm font-medium px-4 py-2 rounded-lg whitespace-nowrap">
            {searching ? 'Searching…' : 'Search'}
          </button>
        </div>
        <div className="flex gap-2">
          <input
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()}
            placeholder="…or User ID"
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm w-32"
          />
          <button onClick={() => load()} className="bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium px-4 py-2 rounded-lg whitespace-nowrap">
            {loading ? 'Loading…' : 'Look up'}
          </button>
        </div>
      </div>

      {/* Name results */}
      {results.length > 0 && (
        <div className="mb-6 bg-white border border-gray-200 rounded-xl divide-y divide-gray-100 max-h-72 overflow-y-auto">
          {results.map((u: any) => {
            const n = [u.details?.first_name, u.details?.last_name].filter(Boolean).join(' ') || '(no name)';
            return (
              <button key={u.id} onClick={() => load(String(u.id))} className="w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center justify-between">
                <span className="text-sm text-gray-800">{n} <span className="text-gray-400">#{u.id}</span></span>
                <span className="text-xs text-gray-500">{u.phone_number || '-'} · <span className="capitalize">{u.status}</span></span>
              </button>
            );
          })}
        </div>
      )}

      {d && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900">Profile</h2>
              <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700 capitalize">{d.user.status}</span>
            </div>
            <div className="mt-3 text-sm text-gray-700 space-y-1">
              <div><span className="text-gray-400">Name:</span> {name || '-'}</div>
              <div><span className="text-gray-400">Phone:</span> {d.user.phone_number || '-'}</div>
              <div><span className="text-gray-400">Joined:</span> {d.user.created_at ? new Date(d.user.created_at).toLocaleDateString() : '-'}</div>
              <div><span className="text-gray-400">Memberships:</span> {d.team_memberships}</div>
            </div>
            <div className="mt-4 flex gap-2">
              {d.user.status !== 'suspended'
                ? <button onClick={() => ban(true)} className="text-xs bg-red-600 text-white px-3 py-1.5 rounded-lg">Suspend</button>
                : <button onClick={() => ban(false)} className="text-xs bg-green-600 text-white px-3 py-1.5 rounded-lg">Reinstate</button>}
            </div>
          </div>

          {/* Footprint moderation */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-900">Footprint</h2>
              {footprint && (
                <span className={`px-2 py-1 rounded-full text-xs ${fpHidden ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>
                  {fpHidden ? 'Hidden' : 'Visible'}
                </span>
              )}
            </div>
            {!footprint ? <div className="text-sm text-gray-400">—</div> : (
              <>
                <div className="grid grid-cols-2 gap-y-1.5 text-sm text-gray-700">
                  <div><span className="text-gray-400">Matches:</span> {footprint.matches_total}</div>
                  <div><span className="text-gray-400">Hidden:</span> {footprint.matches_hidden}</div>
                  <div><span className="text-gray-400">Organized:</span> {footprint.matches_organized}</div>
                  <div><span className="text-gray-400">Scored:</span> {footprint.matches_scored}</div>
                  <div><span className="text-gray-400">Teams:</span> {footprint.teams}</div>
                  <div><span className="text-gray-400">Tournaments:</span> {footprint.tournaments}</div>
                  <div><span className="text-gray-400">Players:</span> {footprint.players}</div>
                </div>
                <div className="mt-4">
                  {!fpHidden
                    ? <button onClick={() => hideFootprint(true)} disabled={hiding} className="text-xs bg-orange-600 hover:bg-orange-700 text-white px-3 py-1.5 rounded-lg disabled:opacity-60">
                        {hiding ? 'Hiding…' : 'Hide all footprint'}
                      </button>
                    : <button onClick={() => hideFootprint(false)} disabled={hiding} className="text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg disabled:opacity-60">
                        {hiding ? 'Restoring…' : 'Restore footprint'}
                      </button>}
                  <p className="mt-2 text-xs text-gray-400">Reversible. Hides matches (organized or scored), teams and their players from stats, leaderboards and listings.</p>
                </div>
              </>
            )}
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h2 className="text-sm font-semibold text-gray-900 mb-2">Name history</h2>
            <div className="text-sm text-gray-600 space-y-1 max-h-40 overflow-y-auto">
              {(d.name_history || []).length === 0 ? <div className="text-gray-400">None</div> :
                d.name_history.map((h: any) => <div key={h.id}>{[h.first_name, h.last_name].filter(Boolean).join(' ') || h.full_name} <span className="text-xs text-gray-400">· {new Date(h.created_at).toLocaleDateString()}</span></div>)}
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h2 className="text-sm font-semibold text-gray-900 mb-2">Owned teams</h2>
            <div className="text-sm text-gray-600 space-y-1">
              {(d.owned_teams || []).length === 0 ? <div className="text-gray-400">None</div> :
                d.owned_teams.map((t: any) => <div key={t.id}>#{t.id} {t.name} {!t.is_active && <span className="text-xs text-gray-400">(inactive)</span>}</div>)}
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h2 className="text-sm font-semibold text-gray-900 mb-2">Organized tournaments</h2>
            <div className="text-sm text-gray-600 space-y-1">
              {(d.organized_tournaments || []).length === 0 ? <div className="text-gray-400">None</div> :
                d.organized_tournaments.map((t: any) => <div key={t.id}>#{t.id} {t.name} <span className="text-xs text-gray-400">· {t.status}</span></div>)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default User360;
